import { formatDistanceStrict } from 'date-fns';
import { colors } from 'ui/theme';
import { match } from 'ts-pattern';
import { flow } from 'fp-ts/function';
import { Mapping } from 'api/MappingsAPI';
import { getFormattedEventDisplayName } from 'modules/predictiveGoals/components/queryBuilder/utils/utils';
import { DataContextMenu } from 'ui/dataContextMenu';
import { MultipleUsers16 } from 'ui/icons/aurora/16px/MultipleUsers16';
import { DataContextContentFieldType } from 'ui/dataContextMenu/types';
import { EXCLUDED_SYSTEM_EVENTS_DELTANAMES } from './constants';
import {
  EventOption,
  Nullable,
  PredictiveAudiencesVocabulary,
  PropertyOption,
  VocabEvents,
  VocabularyEvent,
  VocabularyUserFeature,
} from './types';
import { removeIterablePrefixFromString, parsePropertyValuesFromPropertyOptions } from './utils';

export const ITBLDS_PREFIX_DS = 'itblds_';
export const ITBLDS_PREFIX_ES = 'itblds.';

export const sortByDisplayName = (name1: string, name2: string) =>
  removeIterablePrefixFromString(name1).toLowerCase() <
  removeIterablePrefixFromString(name2).toLowerCase()
    ? -1
    : 1;

/** Property */
export const parsePropertyOptionsFromVocabulary = (
  vocabulary: Nullable<PredictiveAudiencesVocabulary>,
): PropertyOption[] => {
  const propertyOptions: PropertyOption[] = [];
  if (!vocabulary) {
    return propertyOptions;
  }

  const alphabetizedPropertyOptionsByName = vocabulary?.userFeatures?.sort((v1, v2) =>
    sortByDisplayName(v1.displayName, v2.displayName),
  );

  alphabetizedPropertyOptionsByName?.forEach((property) => {
    propertyOptions.push({
      id: property.deltaName,
      label: removeIterablePrefixFromString(property.displayName),
      value: property.deltaName,
      Suffix: property.dataType,
      values: parsePropertyValuesFromPropertyOptions(property.values),
      dataType: property.dataType,
    });
  });
  return propertyOptions;
};

type getRelativeTimeStringProps = {
  laterDate: Date;
  earlierDate: Date;
};

function testBaseStringDate(input: string) {
  return input.replace(/^1\s(.*)$/, 'a $1');
}
function testSeconds(input: string) {
  return input.replace(/(.*)(seconds.*)/, 'a few $2');
}
function testHours(input: string) {
  return input.replace(/a.*(hour ago)/, 'an $1');
}
function testYear(input: string) {
  return input.replace(/1.*(year ago)/, 'a $1');
}
function testYears(input: string) {
  return input.replace(/(.*)(years.*)/, 'more than a year ago');
}

const formatDateDistance = flow(testBaseStringDate, testSeconds, testHours, testYear, testYears);
/**
 *
 * @param laterDate javascript Date object of converted later timestamp
 * @param earlierDate javascript Date object of converted earlier timestamp
 * @returns uses date fns to calculate the differences. string examples include a day ago, a month ago etc.
 *
 * Please see Aurora relative dates for guidance
 * https://aurora.iterable.com/3fe730a39/v/0/p/2920d7-dates-and-times/b/0273e1/t/936701
 */
export const getFormattedDateDistance = ({
  laterDate,
  earlierDate,
}: getRelativeTimeStringProps) => {
  const relativeDays = formatDistanceStrict(earlierDate, laterDate, { addSuffix: true });
  return formatDateDistance(relativeDays);
};

export const convertItbldsPrefixToEsPrefix = (input: string): string => {
  const lowerCasedInput = input.toLowerCase();
  if (lowerCasedInput.startsWith(ITBLDS_PREFIX_DS)) {
    return lowerCasedInput.replaceAll(ITBLDS_PREFIX_DS, ITBLDS_PREFIX_ES);
  }
  return input; // Return the string unchanged if it doesn't start with "itblds_"
};

export const getMappingByPropertyName = (
  fieldName: string,
  mappings: Mapping[] | undefined,
): Mapping | undefined => {
  return (mappings || []).find(
    (mapping) => mapping.fieldName.toLowerCase() === fieldName.toLowerCase(),
  );
};

export const mapVocabEventTypeToDataContextEventType = (
  vocabEvent: VocabEvents,
): DataContextContentFieldType => {
  const mapping: Record<VocabEvents, DataContextContentFieldType> = {
    [VocabEvents.CUSTOM]: DataContextContentFieldType.CUSTOM,
    [VocabEvents.SYSTEM]: DataContextContentFieldType.SYSTEM,
    [VocabEvents.PURCHASE]: DataContextContentFieldType.PURCHASE,
    [VocabEvents.USER]: DataContextContentFieldType.USER,
  };
  return mapping[vocabEvent];
};

export const parsePropertyOptionsFromVocabularyWithOverflow = (
  vocabulary: Nullable<PredictiveAudiencesVocabulary>,
  mappings: Mapping[] | undefined,
): PropertyOption[] => {
  const propertyOptions: PropertyOption[] = [];
  if (!vocabulary) {
    return propertyOptions;
  }

  const lastRefreshDate = vocabulary.computedFor;
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const alphabetizedPropertyOptionsByName = vocabulary?.userFeatures?.sort((v1, v2) =>
    sortByDisplayName(v1.displayName, v2.displayName),
  );

  alphabetizedPropertyOptionsByName?.forEach((property) => {
    const propertyName = property.fieldName
      ? property.fieldName
      : convertItbldsPrefixToEsPrefix(property.deltaName);
    const propertyMetaData = getMappingByPropertyName(propertyName, mappings);

    propertyOptions.push({
      id: property.deltaName,
      label: removeIterablePrefixFromString(property.displayName),
      value: property.deltaName,
      Suffix: property.dataType,
      values: parsePropertyValuesFromPropertyOptions(property.values),
      dataType: property.dataType,
      OverflowContent: property?.type
        ? DataContextMenu({
            event: mapVocabEventTypeToDataContextEventType(property.type),
            description: propertyMetaData?.description,
            fieldDisplayName: removeIterablePrefixFromString(property.displayName),
            sampleValue: propertyMetaData?.sampleValue,
            fieldHeader: {
              icon: MultipleUsers16({ color: colors.grey50 }),
              text: 'Users with this field',
            },
            isError: false,
            isLoading: false,
            fieldData: [{ count: property.userCountMostRecent, text: 'project users' }],
            fieldFooter: { date: new Date(lastRefreshDate), text: 'Usage metrics updated' },
            isLastRefreshTooltipEnabled: false,
          })
        : null,
    });
  });
  return propertyOptions;
};

/** Event */
export const parseEventOptionsFromVocabulary = (
  vocabulary: Nullable<PredictiveAudiencesVocabulary>,
): EventOption[] => {
  const eventOptions: EventOption[] = [];
  if (!vocabulary) {
    return eventOptions;
  }

  const { customEvents = [], purchaseEvents = [], systemEvents = [] } = vocabulary || {};

  const alphabetizedEventOptionsByName = [...customEvents, ...purchaseEvents, ...systemEvents].sort(
    (v1, v2) => sortByDisplayName(v1.displayName, v2.displayName),
  );

  alphabetizedEventOptionsByName.forEach((event) => {
    if (!EXCLUDED_SYSTEM_EVENTS_DELTANAMES.includes(event.deltaName)) {
      eventOptions.push({
        id: event.deltaName,
        label: removeIterablePrefixFromString(event.displayName),
        value: event.deltaName,
        Suffix: event.type,
        type: event.type,
      });
    }
  });
  return eventOptions;
};

export const parseEventOptionsFromVocabularyWithOverflow = (
  vocabulary: Nullable<PredictiveAudiencesVocabulary>,
): EventOption[] => {
  const eventOptions: EventOption[] = [];
  if (!vocabulary) {
    return eventOptions;
  }

  const lastRefreshDate = vocabulary.computedFor;
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const { customEvents = [], purchaseEvents = [], systemEvents = [] } = vocabulary || {};

  const alphabetizedEventOptionsByName = [...customEvents, ...purchaseEvents, ...systemEvents].sort(
    (v1, v2) => sortByDisplayName(v1.displayName, v2.displayName),
  );

  alphabetizedEventOptionsByName.forEach((event) => {
    if (!EXCLUDED_SYSTEM_EVENTS_DELTANAMES.includes(event.deltaName)) {
      eventOptions.push({
        id: event.deltaName,
        label: removeIterablePrefixFromString(event.displayName),
        value: event.deltaName,
        Suffix: getFormattedEventDisplayName(event.type),
        type: event.type,
        OverflowContent: event?.type
          ? DataContextMenu({
              event: mapVocabEventTypeToDataContextEventType(event.type),
              fieldDisplayName: removeIterablePrefixFromString(event.displayName),
              fieldHeader: {
                icon: MultipleUsers16({ color: colors.grey50 }),
                text: 'Users who performed',
              },
              isError: false,
              isLoading: false,
              fieldData: [
                { count: event.userCount1Month, text: 'in the past 30 days' },
                { count: event.userCount13Week, text: 'in the past 90 days' },
              ],
              fieldFooter: { date: new Date(lastRefreshDate), text: 'Usage metrics updated' },
              isLastRefreshTooltipEnabled: false,
            })
          : null,
      });
    }
  });
  return eventOptions;
};

export const mapByVocabEvent = (
  goalVocab: PredictiveAudiencesVocabulary,
  vocabEvent: VocabEvents,
): Array<VocabularyEvent | VocabularyUserFeature> => {
  return match(vocabEvent)
    .with(VocabEvents.CUSTOM, () => goalVocab.customEvents)
    .with(VocabEvents.PURCHASE, () => goalVocab.purchaseEvents)
    .with(VocabEvents.SYSTEM, () => goalVocab.systemEvents)
    .with(VocabEvents.USER, () => goalVocab.userFeatures || [])
    .exhaustive();
};
